import type { ReturnRequestStatusDto } from "@babackapp/private-api-client";
import { TruckIcon } from "@heroicons/react/24/solid";
import { CircleCheck, PackageCheck, PackageOpen, PackageX } from "lucide-react";
import type { ReactNode } from "react";

export type AlertConfig = {
  variant: "default" | "destructive" | "pending" | "info";
  icon: ReactNode;
  titleKey: string;
  descriptionKey: string;
  /**
   * The label and the drop-off point only help while the parcel is still in the
   * customer's hands. Once it travels, they read as an action left to do.
   */
  showShippingActions: boolean;
};

type AlertRule = {
  match: (status: ReturnRequestStatusDto) => boolean;
  config: AlertConfig;
};

// titleKey/descriptionKey are resolved dynamically in status-alert.tsx (t(config.titleKey)),
// so the widget-key scanner can't pick them up via its static t("...") regex. Annotated here
// so they still show up in the dashboard's "Wording du portail" panel.
// @widget-key: returnRequests.details.timeline.cancelled confirmation
// @widget-key: returnRequests.details.status.cancelled.description confirmation
// @widget-key: returnRequests.details.timeline.resolved confirmation
// @widget-key: returnRequests.details.status.resolved.description confirmation
// @widget-key: returnRequests.details.timeline.treatedMerchant confirmation
// @widget-key: returnRequests.details.status.treatedMerchant.description confirmation
// @widget-key: returnRequests.details.timeline.partiallyTreatedMerchant confirmation
// @widget-key: returnRequests.details.timeline.treatedLogistician confirmation
// @widget-key: returnRequests.details.status.treated.description confirmation
// @widget-key: returnRequests.details.timeline.partiallyTreatedLogistician confirmation
// @widget-key: returnRequests.details.status.partiallyTreated.description confirmation
// @widget-key: returnRequests.details.timeline.shipped confirmation
// @widget-key: returnRequests.details.status.shipped.description confirmation
// @widget-key: returnRequests.details.timeline.shipping confirmation
// @widget-key: returnRequests.details.timeline.approved confirmation
// @widget-key: returnRequests.details.status.approved.description confirmation
// @widget-key: returnRequests.details.timeline.waiting confirmation
// @widget-key: returnRequests.details.status.waiting.description confirmation
/**
 * Most advanced status first. The merchant's decision outranks the warehouse
 * reception, which outranks the transit, so a return keeps announcing the
 * furthest point it has reached.
 */
const ALERT_RULES: AlertRule[] = [
  {
    match: (status) => status.cancelled.done,
    config: {
      variant: "destructive",
      icon: <PackageX />,
      titleKey: "returnRequests.details.timeline.cancelled",
      descriptionKey: "returnRequests.details.status.cancelled.description",
      showShippingActions: false,
    },
  },
  {
    match: (status) => status.resolved.done,
    config: {
      variant: "default",
      icon: <PackageCheck />,
      titleKey: "returnRequests.details.timeline.resolved",
      descriptionKey: "returnRequests.details.status.resolved.description",
      showShippingActions: false,
    },
  },
  {
    match: (status) => status.treatedMerchant.done,
    config: {
      variant: "default",
      icon: <CircleCheck />,
      titleKey: "returnRequests.details.timeline.treatedMerchant",
      descriptionKey: "returnRequests.details.status.treatedMerchant.description",
      showShippingActions: false,
    },
  },
  {
    match: (status) => status.partiallyTreatedMerchant.done,
    config: {
      variant: "info",
      icon: <PackageOpen />,
      titleKey: "returnRequests.details.timeline.partiallyTreatedMerchant",
      descriptionKey: "returnRequests.details.status.partiallyTreated.description",
      showShippingActions: false,
    },
  },
  {
    match: (status) => status.treatedLogistician.done,
    config: {
      variant: "info",
      icon: <PackageCheck />,
      titleKey: "returnRequests.details.timeline.treatedLogistician",
      descriptionKey: "returnRequests.details.status.treated.description",
      showShippingActions: false,
    },
  },
  {
    match: (status) => status.partiallyTreatedLogistician.done,
    config: {
      variant: "info",
      icon: <PackageOpen />,
      titleKey: "returnRequests.details.timeline.partiallyTreatedLogistician",
      descriptionKey: "returnRequests.details.status.partiallyTreated.description",
      showShippingActions: false,
    },
  },
  {
    match: (status) => status.shipped.done,
    config: {
      variant: "info",
      icon: <TruckIcon />,
      titleKey: "returnRequests.details.timeline.shipped",
      descriptionKey: "returnRequests.details.status.shipped.description",
      showShippingActions: false,
    },
  },
  {
    match: (status) => status.shipping.done,
    config: {
      variant: "pending",
      icon: <TruckIcon />,
      titleKey: "returnRequests.details.timeline.shipping",
      descriptionKey: "returnRequests.details.status.approved.description",
      showShippingActions: true,
    },
  },
  {
    match: (status) => status.approved.done,
    config: {
      variant: "pending",
      icon: <TruckIcon />,
      titleKey: "returnRequests.details.timeline.approved",
      descriptionKey: "returnRequests.details.status.approved.description",
      showShippingActions: true,
    },
  },
  {
    match: (status) => status.waiting.done,
    config: {
      variant: "pending",
      icon: <TruckIcon />,
      titleKey: "returnRequests.details.timeline.waiting",
      descriptionKey: "returnRequests.details.status.waiting.description",
      showShippingActions: true,
    },
  },
];

export function resolveAlertConfig(status: ReturnRequestStatusDto): AlertConfig | null {
  return ALERT_RULES.find((rule) => rule.match(status))?.config ?? null;
}
